import React, { useEffect, useState } from 'react';
import { analyticsApi } from '../api';
import { Send, Eye, MessageSquare, AlertTriangle, Loader2 } from 'lucide-react';

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsApi.dashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A73E8]" /></div>;

  const d = data || {};
  const openRate = d.total_sent ? ((d.total_opened / d.total_sent) * 100).toFixed(1) : '0';
  const clickRate = d.total_sent ? ((d.total_clicked / d.total_sent) * 100).toFixed(1) : '0';
  const replyRate = d.total_sent ? ((d.total_replied / d.total_sent) * 100).toFixed(1) : '0';
  const bounceRate = d.total_sent ? ((d.total_bounced / d.total_sent) * 100).toFixed(1) : '0';

  return (
    <div data-testid="outreach-analytics">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#202124]">Analytics</h1>
        <p className="text-sm text-[#5F6368]">Detailed performance metrics</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Sent', value: d.total_sent || 0, icon: Send, color: 'bg-[#1A73E8]', sub: `${d.sent_today || 0} today` },
          { label: 'Open Rate', value: `${openRate}%`, icon: Eye, color: 'bg-[#1E8E3E]', sub: `${d.total_opened || 0} opens` },
          { label: 'Reply Rate', value: `${replyRate}%`, icon: MessageSquare, color: 'bg-[#7C3AED]', sub: `${d.total_replied || 0} replies` },
          { label: 'Bounce Rate', value: `${bounceRate}%`, icon: AlertTriangle, color: 'bg-[#D93025]', sub: `${d.total_bounced || 0} bounced` },
        ].map(({ label, value, icon: Icon, color, sub }) => (
          <div key={label} className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-4 h-4 text-white" /></div>
              <span className="text-sm text-[#5F6368] font-medium">{label}</span>
            </div>
            <p className="text-3xl font-bold text-[#202124]">{value}</p>
            {sub && <p className="text-xs text-[#9AA0A6] mt-1">{sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-[#202124] mb-4">Additional Metrics</h3>
          <div className="space-y-3">
            {[
              { label: 'Click Rate', value: `${clickRate}%`, color: 'bg-[#0891B2]' },
              { label: 'Unsubscribed', value: d.total_unsubscribed || 0, color: 'bg-[#D81B60]' },
              { label: 'Active Campaigns', value: d.active_campaigns || 0, color: 'bg-[#1E8E3E]' },
              { label: 'Total Leads', value: d.total_leads || 0, color: 'bg-[#6366F1]' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between py-2 border-b border-[#F1F3F4] last:border-0">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                  <span className="text-sm text-[#5F6368]">{label}</span>
                </div>
                <span className="text-sm font-semibold text-[#202124]">{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-[#202124] mb-4">Emails Sent (Last 30 Days)</h3>
          {d.daily_sends?.length > 0 ? (
            <div className="flex items-end gap-1 h-40">
              {d.daily_sends.map((day, i) => {
                const max = Math.max(...d.daily_sends.map(s => s.count));
                const h = max ? (day.count / max) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center" title={`${day.date}: ${day.count}`}>
                    <div className="w-full bg-[#1A73E8] rounded-t opacity-80 hover:opacity-100 transition-opacity" style={{ height: `${Math.max(h, 2)}%` }} />
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-[#9AA0A6] text-center py-8">No data yet</p>
          )}
        </div>
      </div>

      {d.top_campaigns?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden mt-6">
          <div className="px-5 py-4 border-b border-[#F1F3F4]">
            <h3 className="text-sm font-semibold text-[#202124]">Campaign Performance</h3>
          </div>
          <div className="p-5 space-y-3">
            {d.top_campaigns.map((c) => {
              const maxSent = Math.max(...d.top_campaigns.map(x => x.sent));
              const w = maxSent ? (c.sent / maxSent) * 100 : 0;
              return (
                <div key={c.id}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[#202124] font-medium truncate">{c.name}</span>
                    <span className="text-[#5F6368]">{c.sent} sent</span>
                  </div>
                  <div className="w-full bg-[#F1F3F4] rounded-full h-2">
                    <div className="bg-[#1A73E8] h-2 rounded-full transition-all" style={{ width: `${w}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
